import { UppFloorPlanArchitecture } from '../../floor-plan-element';
import type { FloorPlanAreaSourceDrawPieceItem, UppKonvaShape } from '../floor-plan-wire-types';
import { elementOptsFromLabels } from './shape-bridge-helpers';
import type { FloorPlanShapeBridge, SourceToWireInput } from './shape-bridge-types';

function adapterRowLabels(row: UppKonvaShape): {
  label?: string;
  sublabel?: string;
  zIndex?: number;
  custom?: unknown;
} {
  const o: { label?: string; sublabel?: string; zIndex?: number; custom?: unknown } = {};
  if ('label' in row && row.label !== undefined) {
    o.label = row.label;
  }
  if ('sublabel' in row && row.sublabel !== undefined) {
    o.sublabel = row.sublabel;
  }
  if ('zIndex' in row && row.zIndex !== undefined) {
    o.zIndex = row.zIndex;
  }
  if (Object.prototype.hasOwnProperty.call(row, 'custom')) {
    o.custom = (row as { custom?: unknown }).custom;
  }
  return o;
}

function createArchitectureBridge(
  kind: 'DOOR' | 'WINDOW' | 'STAIRS',
  wireType: 'DOOR' | 'WINDOW' | 'STAIRS',
  architectureClass: 'door' | 'window' | 'stairs',
): FloorPlanShapeBridge {
  return {
    kind,
    wireType,
    sourceToWire(i: SourceToWireInput): UppKonvaShape {
      const p: FloorPlanAreaSourceDrawPieceItem = {
        type: wireType,
        x: i.x,
        y: i.y,
        width: i.width,
        height: i.height,
        rotation: i.rotation,
        architectureShape: String(i.d['shape'] ?? ''),
        ...(i.id.uuid ? { uuid: i.id.uuid } : {}),
        ...i.lb,
      };
      return { ...p, ...(!i.id.uuid ? { id: i.id.id } : {}) } as UppKonvaShape;
    },
    wireToSource(row: UppKonvaShape) {
      const r = row as FloorPlanAreaSourceDrawPieceItem;
      const uuid = r.uuid !== undefined && r.uuid.trim() !== '' ? r.uuid.trim() : '';
      const legacyId = 'id' in row && typeof (row as { id?: unknown }).id === 'string' ? String((row as { id: string }).id) : '';
      const id = uuid || legacyId || `shape-${wireType}`;
      const base = {
        id,
        x: r.x,
        y: r.y,
        width: r.width,
        height: r.height,
        rotation: r.rotation,
        ...adapterRowLabels(row),
      };
      return {
        ...base,
        kind,
        data: { shape: r.architectureShape ?? '' },
      };
    },
    wireToElement(row: UppKonvaShape, uuid: string) {
      const r = row as FloorPlanAreaSourceDrawPieceItem;
      const { x, y, width, height, rotation } = r;
      const opt = elementOptsFromLabels(r, false);
      const el = new UppFloorPlanArchitecture(
        uuid,
        x,
        y,
        width,
        height,
        rotation,
        architectureClass,
        r.architectureShape,
        opt,
      );
      el.zIndex = r.zIndex ?? 0;
      return el;
    },
  };
}

export const doorShapeBridge = createArchitectureBridge('DOOR', 'DOOR', 'door');
export const windowShapeBridge = createArchitectureBridge('WINDOW', 'WINDOW', 'window');
export const stairsShapeBridge = createArchitectureBridge('STAIRS', 'STAIRS', 'stairs');
